import { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { generatePersonalizedFeed } from '../../utils/feedAlgorithm';
import RecipeCard from './cards/RecipeCard';
import ActivityCard from './cards/ActivityCard';
import StoryCard from './cards/StoryCard';
import ContinueExperienceCard from './cards/ContinueExperienceCard';
import StartExperienceCard from './cards/StartExperienceCard';
import ResumeChatCard from './cards/ResumeChatCard';
import AIStarterCard from './cards/AIStarterCard';

export default function PersonalizedFeed({ family, experiences, conversations }) {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [columnCount, setColumnCount] = useState(5);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const containerRef = useRef(null);
  const itemRefs = useRef({});

  // Memoize feed generation to avoid recalculating on every render
  const feedItems = useMemo(() => {
    if (!family) return [];
    
    const startTime = performance.now();
    const items = generatePersonalizedFeed(family, experiences, conversations, 20);
    const endTime = performance.now();
    
    // Log performance (only in development)
    if (process.env.NODE_ENV === 'development') {
      console.log(`Feed generation took ${(endTime - startTime).toFixed(2)}ms`);
    }
    
    return items;
  }, [family, experiences, conversations]);

  useEffect(() => {
    if (!family) {
      setLoading(false);
      return;
    }

    // Use requestAnimationFrame to prevent blocking the UI
    const rafId = requestAnimationFrame(() => {
      setFeed(feedItems);
      setLoading(false);
    });

    return () => cancelAnimationFrame(rafId);
  }, [feedItems, family]);

  // Calculate column count based on container width and minimum card width
  useEffect(() => {
    const updateColumnCount = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.offsetWidth;
      const minCardWidth = 270; // Minimum card width in pixels
      const gap = 8; // Gap between columns (reduced for tighter spacing)
      
      // Mobile: always single column for better readability
      if (width < 768) {
        setColumnCount(1);
        return;
      }
      
      // Calculate max columns based on minimum card width
      // Formula: width >= n * minCardWidth + (n-1) * gap
      // Solving for n: n <= (width + gap) / (minCardWidth + gap)
      const maxColumns = Math.floor((width + gap) / (minCardWidth + gap));
      
      // Ensure at least 1 column, and cap at 5 for very large screens
      const columnCount = Math.max(1, Math.min(maxColumns, 5));
      
      // Debug logging (remove in production if needed)
      if (process.env.NODE_ENV === 'development') {
        console.log(`Container width: ${width}px, Calculated columns: ${columnCount}, Min card width: ${minCardWidth}px`);
      }
      
      setColumnCount(columnCount);
    };

    // Initial calculation
    updateColumnCount();
    
    // Recalculate on resize with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateColumnCount, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Distribute items into columns based on height (masonry layout)
  useLayoutEffect(() => {
    if (feed.length === 0 || loading) {
      setColumns([]);
      setIsMeasuring(false);
      return;
    }

    // Start measuring - render items first, then measure and redistribute
    setIsMeasuring(true);
    
    let timeoutId;
    let retryTimeoutId;
    
    // Wait for items to render and measure their heights
    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        const newColumns = Array.from({ length: columnCount }, () => []);
        const columnHeights = Array(columnCount).fill(0);

        let allMeasured = true;
        feed.forEach((item, index) => {
          const itemKey = `${item.type}-${item.id || index}`;
          const itemElement = itemRefs.current[itemKey];
          
          if (itemElement && itemElement.offsetHeight > 0) {
            // Find the shortest column
            const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
            
            // Add item to shortest column
            newColumns[shortestColumnIndex].push(item);
            
            // Update column height (accounting for gap)
            const itemHeight = itemElement.offsetHeight;
            columnHeights[shortestColumnIndex] += itemHeight + 24; // 24px = gap-y-6 (1.5rem)
          } else {
            allMeasured = false;
            // Fallback: distribute evenly if element not found or not measured yet
            const columnIndex = index % columnCount;
            newColumns[columnIndex].push(item);
          }
        });

        // Always set columns (even if some measurements failed, we have fallback distribution)
        setColumns(newColumns);
        setIsMeasuring(false);
      }, 100);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, [feed, columnCount, loading]);

  const renderCard = (item, index) => {
    const itemKey = `${item.type}-${item.id || index}`;
    
    switch (item.type) {
      case 'recipe':
        return (
          <div 
            key={`recipe-${item.id || index}`} 
            ref={el => { if (el) itemRefs.current[itemKey] = el; }}
            className="mb-6"
          >
            <RecipeCard recipe={item.data} />
          </div>
        );
      case 'activity':
        return (
          <div 
            key={`activity-${item.id || index}`} 
            ref={el => { if (el) itemRefs.current[itemKey] = el; }}
            className="mb-6"
          >
            <ActivityCard activity={item.data} />
          </div>
        );
      case 'story':
        return (
          <div 
            key={`story-${item.id || index}`} 
            ref={el => { if (el) itemRefs.current[itemKey] = el; }}
            className="mb-6"
          >
            <StoryCard story={item.data} />
          </div>
        );
      case 'continue-experience':
        return (
          <div 
            key={`exp-${item.id}`} 
            ref={el => { if (el) itemRefs.current[itemKey] = el; }}
            className="mb-6"
          >
            <ContinueExperienceCard experience={item.data} />
          </div>
        );
      case 'start-experience':
        return (
          <div 
            key={`start-${index}`} 
            ref={el => { if (el) itemRefs.current[itemKey] = el; }}
            className="mb-6"
          >
            <StartExperienceCard suggestion={item.data} />
          </div>
        );
      case 'resume-chat':
        return (
          <div 
            key={`chat-${item.id}`} 
            ref={el => { if (el) itemRefs.current[itemKey] = el; }}
            className="mb-6"
          >
            <ResumeChatCard chat={item.data} />
          </div>
        );
      case 'ai-starter':
        return (
          <div 
            key={`ai-${index}`} 
            ref={el => { if (el) itemRefs.current[itemKey] = el; }}
            className="mb-6"
          >
            <AIStarterCard starter={item.data} />
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="mt-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-6 items-start">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-card border-2 border-border p-6 animate-pulse max-w-sm mx-auto">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (feed.length === 0) {
    return null;
  }

  return (
    <section className="mt-6 md:mt-12" aria-label="Personalized content feed">
      <h2 className="text-xl md:text-2xl font-serif font-bold text-text-primary mb-4 md:mb-6">
        Personalized for You
      </h2>
      <div 
        ref={containerRef}
        className="grid gap-2 md:gap-3"
        role="list"
        style={{ 
          gridTemplateColumns: `repeat(auto-fit, minmax(270px, 1fr))`,
          minWidth: 0 
        }}
      >
        {columns.length > 0 && !isMeasuring ? (
          // Render masonry columns
          columns.map((column, colIndex) => (
            <div 
              key={colIndex} 
              className="flex flex-col"
            >
              {column.map((item, itemIndex) => {
                const globalIndex = feed.findIndex(f => 
                  f.type === item.type && (f.id === item.id || f === item)
                );
                return renderCard(item, globalIndex >= 0 ? globalIndex : itemIndex);
              })}
            </div>
          ))
        ) : (
          // Render in a grid for measurement (items will be measured at correct width)
          // Then redistributed into masonry columns
          Array.from({ length: columnCount }, (_, colIndex) => (
            <div 
              key={`measure-col-${colIndex}`}
              className="flex flex-col"
            >
              {feed
                .filter((_, index) => index % columnCount === colIndex)
                .map((item, filteredIndex) => {
                  const originalIndex = colIndex + filteredIndex * columnCount;
                  const itemKey = `${item.type}-${item.id || originalIndex}`;
                  return (
                    <div 
                      key={`measure-${itemKey}`}
                      ref={el => { if (el) itemRefs.current[itemKey] = el; }}
                      className="mb-6"
                    >
                      {renderCard(item, originalIndex)}
                    </div>
                  );
                })}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
